import React, { FC } from 'react';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { BiChevronsLeft, BiChevronLeft, BiChevronRight, BiChevronsRight } from 'react-icons/bi';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import Button from '../Button';

export interface IProps {
  gotoPage: (x: number) => void;
  canPreviousPage: boolean;
  previousPage: () => void;
  nextPage: () => void;
  canNextPage: boolean;
  pageCount: number;
  pageIndex: number;
  pageOptions: number[];
  pageSize: number;
  total: number;
  setPageSize: (x: number) => void;
  disablePagination?: boolean;
}

/*
 * `Pagination` implements row pagination through `usePagination` react-table hook.
 *
 * Doc: https://react-table.tanstack.com/docs/api/usePagination
 */
const Pagination: FC<IProps> = ({
  gotoPage,
  canPreviousPage,
  previousPage,
  nextPage,
  canNextPage,
  pageCount,
  pageIndex,
  pageOptions,
  pageSize,
  total,
  setPageSize,
  disablePagination = false,
}: IProps) => {
  const { t } = useTranslation();
  const startIndex = pageIndex * pageSize + 1;
  const endIndex = Math.min(startIndex + pageSize - 1, total);

  // Visible pages navigation
  // Set visible pages variable
  const visiblePages = 5;
  const halfVisiblePages = Math.floor(visiblePages / 2);

  // Center the window on `pageIndex`, then clamp it into [0, pageCount]
  // so it never shifts to the last/first pages just because `pageIndex`
  // crossed a fixed threshold (that shift must depend on `pageCount`).
  let startPageIndex = pageIndex - halfVisiblePages;
  let endPageIndex = pageIndex + halfVisiblePages + 1;

  if (startPageIndex < 0) {
    endPageIndex += -startPageIndex;
    startPageIndex = 0;
  }
  if (endPageIndex > pageCount) {
    startPageIndex -= endPageIndex - pageCount;
    endPageIndex = pageCount;
  }
  startPageIndex = Math.max(startPageIndex, 0);

  const pagesToShow = pageOptions.slice(startPageIndex, endPageIndex);

  return (
    <FlexWrapper>
      <PaginationWrapper>
        <TooltipWrapper
          id="tooltip-first"
          overlay={t('table.firstPage')}
        >
          <Button
            size="sm"
            btnType="secondary"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage || disablePagination}
          >
            <BiChevronsLeft size={20} />
          </Button>
        </TooltipWrapper>
        <TooltipWrapper
          id="tooltip-prev"
          overlay={t('table.prevPage')}
        >
          <Button
            size="sm"
            btnType="secondary"
            className="ml-1 mr-1"
            onClick={() => previousPage()}
            disabled={!canPreviousPage || disablePagination}
          >
            <BiChevronLeft size={20} />
          </Button>
        </TooltipWrapper>
        {startPageIndex > 0 && (
          <TooltipWrapper
            id="tooltip-more-first"
            overlay={t('table.morePages')}
          >
            <Button
              size="sm"
              btnType="light"
              outlined
              className="ml-1 mr-1"
              onClick={() => gotoPage(Math.max(pageIndex - visiblePages, 0))}
            >
              ...
            </Button>
          </TooltipWrapper>
        )}
        {pagesToShow.map((page: number, index: number) => {
          return (
            <Button
              key={index}
              size="sm"
              btnType={page === pageIndex ? 'secondary' : 'light'}
              outlined
              className="ml-1 mr-1"
              onClick={() => gotoPage(page)}
              disabled={disablePagination}
            >
              {page + 1}
            </Button>
          );
        })}
        {endPageIndex < pageCount && (
          <TooltipWrapper
            id="tooltip-more-last"
            overlay={t('table.morePages')}
          >
            <Button
              size="sm"
              btnType="light"
              outlined
              className="ml-1 mr-1"
              onClick={() => gotoPage(Math.min(pageIndex + visiblePages, pageCount - 1))}
            >
              ...
            </Button>
          </TooltipWrapper>
        )}
        <TooltipWrapper
          id="tooltip-next"
          overlay={t('table.nextPage')}
        >
          <Button
            size="sm"
            btnType="secondary"
            className="ml-1 mr-1"
            onClick={() => nextPage()}
            disabled={!canNextPage || disablePagination}
          >
            <BiChevronRight size={20} />
          </Button>
        </TooltipWrapper>
        <TooltipWrapper
          id="tooltip-last"
          overlay={t('table.lastPage')}
        >
          <Button
            size="sm"
            btnType="secondary"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage || disablePagination}
          >
            <BiChevronsRight size={20} />
          </Button>
        </TooltipWrapper>
      </PaginationWrapper>
      <FlexWrapperInner>
        <Form.Control
          as="select"
          value={pageSize}
          size="sm"
          className="c-page-size-control mr-3"
          onChange={e => setPageSize(Number(e.target.value))}
          disabled={disablePagination}
        >
          {[10, 20, 30, 50, 100].map(pageSizeVal => (
            <option
              key={pageSizeVal}
              value={pageSizeVal}
            >
              {pageSizeVal}
            </option>
          ))}
        </Form.Control>
        <div>
          {pageCount === 1
            ? t(total === 1 ? 'table.allResultsShown' : 'table.allResultsShown_plural', { total })
            : t('table.resultsShown', { startIndex, endIndex, total })}
        </div>
      </FlexWrapperInner>
    </FlexWrapper>
  );
};

const FlexWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;

  @media ${props => props.theme.breakpoints.sm} {
    flex-direction: column-reverse;
    align-items: flex-start;
  }
`;

const FlexWrapperInner = styled.div`
  display: flex;
  align-items: center;
`;

const PaginationWrapper = styled.div`
  margin: 16px 0;
  align-self: center;
`;

export default Pagination;
